import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';
import { configureAmplify, loadRemoteConfig } from './config/amplify';
import { initAnalytics } from './services/analytics';
import { initTawkTo } from './services/tawkto';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import ToastContainer from './components/ToastContainer';
import { fetchAuthSession } from 'aws-amplify/auth';

// Initialize app
async function initApp() {
  // Load remote config if available
  await loadRemoteConfig();

  // Configure AWS Amplify
  configureAmplify();

  // Initialize Google Analytics
  initAnalytics();

  // Initialize tawk.to chat widget
  initTawkTo();

  // Add global helper for testing from console
  (window as any).seedTournament = async function () {
    const API_URL = import.meta.env.VITE_API_URL;

    console.log('🌱 Seeding tournament...');

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        console.error('%c ❌ Not logged in', 'color: #ef4444; font-size: 16px; font-weight: bold');
        console.log('Please log in first, then try again.');
        return;
      }

      const response = await fetch(`${API_URL}/tournaments/seed`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to seed tournament');
      }

      console.log(
        '%c ✅ Success! Tournament Created!',
        'color: #10b981; font-size: 18px; font-weight: bold'
      );
      console.log('📋 Tournament:', data.tournamentName);
      console.log('🆔 ID:', data.tournamentId);
      console.log('🤼 Wrestlers:', data.wrestlerCount);
      console.log('🔗 URL:', data.websiteUrl);
      console.log('\n👉 Opening tournament in new tab...');

      // Store for later use
      (window as any).lastTournamentId = data.tournamentId;

      // Auto-open
      window.open(data.websiteUrl, '_blank');

      return data;
    } catch (error: any) {
      console.error('%c ❌ Error!', 'color: #ef4444; font-size: 18px; font-weight: bold');
      console.error(error.message);
      throw error;
    }
  };

  // Add console message about helper
  console.log(
    '%c 🤼 Takedown League - Test Helpers Available',
    'color: #3b82f6; font-size: 14px; font-weight: bold; padding: 8px; background: #dbeafe; border-radius: 4px;'
  );
  console.log('%c To seed a test tournament, run:', 'color: #6b7280; font-size: 12px;');
  console.log(
    '%c   seedTournament()',
    'color: #10b981; font-size: 14px; font-weight: bold; font-family: monospace; background: #f0fdf4; padding: 4px 8px; border-radius: 4px;'
  );

  // Render app
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <App />
            <ToastContainer />
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </React.StrictMode>
  );
}

// Start the app
initApp();
